"use client";

import { Mail, Loader } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function NewsletterForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="border border-[hsl(0_0%_92%)] bg-[hsl(355_100%_96%_/_0.3)] p-8 text-center">
        <p style={{
        fontFamily: "var(--font-serif)"
      }} className="text-2xl text-[hsl(0_0%_12%)] mb-3">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="ghost" className="text-[hsl(0_100%_71%)] hover:text-[hsl(0_0%_12%)] hover:bg-transparent uppercase tracking-widest text-xs">
          Subscribe Another Address
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <FormField control={control} name="first_name" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[11px] uppercase tracking-[0.2em] text-[hsl(0_0%_40%)] font-medium">
              First Name
            </FormLabel>
            <FormControl>
              <Input placeholder="Jane" {...field} className="h-14 bg-transparent border-0 border-b-2 border-[hsl(0_0%_12%)] text-[hsl(0_0%_12%)] placeholder:text-[hsl(0_0%_40%)] focus-visible:ring-0 focus-visible:border-[hsl(0_100%_71%)] px-0 text-base" />
            </FormControl>
            <FormDescription className="text-[hsl(0_0%_40%)] text-xs">
              Optional — helps us personalize your scoops.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[11px] uppercase tracking-[0.2em] text-[hsl(0_0%_40%)] font-medium">
              Email Address
            </FormLabel>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-0">
              <FormControl>
                <Input type="email" placeholder="hello@scoop.com" {...field} className="h-14 bg-transparent border-0 border-b-2 border-[hsl(0_0%_12%)] text-[hsl(0_0%_12%)] placeholder:text-[hsl(0_0%_40%)] focus-visible:ring-0 focus-visible:border-[hsl(0_100%_71%)] px-0 text-base flex-1" />
              </FormControl>
              <Button type="submit" disabled={isSubmitting} className="h-14 px-8 bg-[hsl(0_100%_71%)] hover:bg-[hsl(0_0%_12%)] text-white uppercase tracking-[0.15em] text-xs font-semibold">
                {isSubmitting ? <>
                    <Loader className="animate-spin mr-2 size-4" />
                    Subscribing
                  </> : <>Subscribe →</>}
              </Button>
            </div>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="consent" render={({
      field
    }) => <FormItem>
            <div className="flex items-start gap-3">
              <FormControl>
                <input type="checkbox" checked={!!field.value} onChange={e => field.onChange(e.target.checked)} onBlur={field.onBlur} name={field.name} ref={field.ref} className="mt-1 size-4 accent-[hsl(0_100%_71%)] cursor-pointer" />
              </FormControl>
              <div className="flex-1">
                <FormLabel className="text-[hsl(0_0%_12%)] text-sm font-normal leading-relaxed cursor-pointer">
                  I agree to receive the SCOOP newsletter and seasonal flavor updates.
                </FormLabel>
                <FormDescription className="text-[hsl(0_0%_40%)] text-xs mt-1">
                  We respect your privacy. You can unsubscribe at any time.
                </FormDescription>
                <FormMessage />
              </div>
            </div>
          </FormItem>} />

      {submitError && <div className="border border-[hsl(0_84%_60%_/_0.4)] bg-[hsl(0_84%_60%_/_0.1)] p-4">
          <p className="text-[hsl(0_84%_60%)] text-sm">{submitError}</p>
        </div>}
    </Form>;
}
export default function Newsletter() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="4180" id="join-the-social-club" className="bg-[hsl(40_33%_98%)] text-[hsl(0_0%_12%)] relative overflow-hidden py-24 md:py-36 px-6">
      {}
      <div aria-hidden="true" className="hidden lg:block absolute right-6 top-1/2 -translate-y-1/2 select-none pointer-events-none" style={{
      transform: "translateY(-50%) rotate(90deg)",
      transformOrigin: "right center",
      fontFamily: "var(--font-sans)",
      letterSpacing: "0.5em",
      fontSize: "11px",
      color: "hsl(0 0% 12% / 0.06)",
      whiteSpace: "nowrap"
    }}>
        SCOOP DISPATCH · SCOOP DISPATCH · SCOOP DISPATCH
      </div>

      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-px bg-[hsl(0_0%_12%_/_0.3)]" />
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-24 h-px bg-[hsl(0_0%_12%_/_0.3)]" />

      <div ref={ref} className={`relative mx-auto max-w-[720px] transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        {}
        <div className="flex justify-center mb-10">
          <div className="relative">
            <div className="absolute inset-0 blur-2xl bg-[hsl(0_100%_71%_/_0.2)]" />
            <div className="relative size-14 border border-[hsl(0_0%_12%)] flex items-center justify-center">
              <Mail strokeWidth={1} className="size-6 text-[hsl(0_0%_12%)]" />
            </div>
          </div>
        </div>

        {}
        <div className="flex items-center justify-center gap-4 mb-8">
          <span className="h-px w-8 bg-[hsl(0_0%_12%_/_0.5)]" />
          <p className="text-[hsl(0_0%_12%)] text-[11px] uppercase tracking-[0.35em] font-medium">
            Monthly Flavors
          </p>
          <span className="h-px w-8 bg-[hsl(0_0%_12%_/_0.5)]" />
        </div>

        {}
        <h2 style={{
        fontFamily: "var(--font-serif)"
      }} className="text-center text-[40px] sm:text-[52px] md:text-[64px] leading-[1.05] text-[hsl(0_0%_12%)] font-medium tracking-tight mb-8">
          Artisanal Scoops,
          <br />
          <span className="italic text-[hsl(0_100%_71%)] font-normal" style={{
          fontFamily: "var(--font-serif)"
        }}>
            Delivered Freshly.
          </span>
        </h2>

        {}
        <p style={{
        fontFamily: "var(--font-sans)"
      }} className="text-center text-[17px] leading-[1.7] text-[hsl(0_0%_40%)] max-w-[560px] mx-auto mb-12 font-normal">
          A monthly curation for the true ice cream enthusiast.
          Receive early access to limited-batch flavors, seasonal
          recipe insights, and quiet invitations to our private
          tasting events — composed for our inner circle.
        </p>

        {}
        <div className="max-w-[560px] mx-auto">
          <FormProvider formKey="social_club_join_home" formId="social_club_join_home" sectionName="join-the-social-club" defaultValues={{
          first_name: "",
          email: "",
          consent: false
        }} validationRules={{
          first_name: {
            maxLength: 50
          },
          email: {
            required: true,
            pattern: "email"
          },
          consent: {
            required: true
          }
        }} submitText="Subscribe" successMessage="Welcome to the inner circle." errorMessage="We could not process your subscription. Please try again.">
            <NewsletterForm />
          </FormProvider>

          {}
          <p className="text-center text-[hsl(0_0%_40%)] text-[11px] mt-8 leading-relaxed tracking-wide">
            Join 1,400+ flavor seekers. Unsubscribe at any time.
            <br className="hidden sm:block" />
            <span className="sm:inline"> We protect your data with the same care as our cream.</span>
          </p>
        </div>
      </div>
    </section>;
}